import axios from 'axios';
import {
  getAccessToken,
  getCurrentLanguage,
  getRefreshToken,
  getCompanyID,
  getUserRolePrivileges,
  getAssignRoleKey,
} from '@/helpers/Utils';

import { loadApiConfig, getApiBaseUrl } from '@/constants/apiConfig';
import { AUTH_REFRESH_TOKEN } from '@/redux/RTK/authService/Endpoints';
import { getTenantCode } from '@/helpers/auth-configration';

// Initialize Axios instance
const CLIENT_SIDE_RESPONSE_CODES = {
  NoInternet: 'NETWORK_ERROR',
  ServerUnreachable: 'SERVER_UNREACHABLE',
  RequestTimeout: 'REQUEST_TIMEOUT',
};

const API_CONNECTIVITY_ERROR_EVENT = 'app-api-connectivity-error';
const API_ROLE_NOT_AVAILABLE_EVENT = 'app-role-not-available';
const API_CONNECTIVITY_CLEAR_EVENT = 'app-api-connectivity-clear';

const FALLBACK_CONNECTIVITY_MESSAGES = {
  en: {
    noInternetTitle: 'No internet connection',
    serverUnreachableTitle: 'Service Unavailable',
    serverErrorTitle: 'Server Error',
    serverUnavailableTitle: 'Service Unavailable',
  },
  ar: {
    noInternetTitle: 'لا يوجد اتصال بالإنترنت',
    serverUnreachableTitle: 'الخدمة غير متوفرة',
    serverErrorTitle: 'خطأ في الخادم',
    serverUnavailableTitle: 'الخدمة غير متوفرة',
  },
};

const loadLanguageMessages = async (language) => {
  if (String(language || '').toLowerCase() === 'ar') {
    const module = await import('../lang/ar');
    return module.default || {};
  }

  const module = await import('../lang/en');
  return module.default || {};
};

const getConnectivityMessages = async () => {
  const language = getCurrentLanguage() === 'ar' ? 'ar' : 'en';
  const fallbacks = FALLBACK_CONNECTIVITY_MESSAGES[language];

  try {
    const messages = await loadLanguageMessages(language);
    return {
      noInternetTitle: messages['appLayout.connectivity.noInternetTitle'] || fallbacks.noInternetTitle,
      noInternetMessage: messages['appLayout.connectivity.noInternetMessage'] || '',
      serverUnreachableTitle: messages['appLayout.connectivity.serverUnreachableTitle'] || fallbacks.serverUnreachableTitle,
      serverUnreachableMessage: messages['appLayout.connectivity.serverUnreachableMessage'] || '',
      serverErrorTitle: messages['appLayout.connectivity.serverErrorTitle'] || fallbacks.serverErrorTitle,
      serverErrorMessage: messages['appLayout.connectivity.serverErrorMessage'] || '',
      serverUnavailableTitle: messages['appLayout.connectivity.serverUnavailableTitle'] || fallbacks.serverUnavailableTitle,
      serverUnavailableMessage: messages['appLayout.connectivity.serverUnavailableMessage'] || '',
    };
  } catch (error) {
    return fallbacks;
  }
};

const emitConnectivityError = (detail) => {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent(API_CONNECTIVITY_ERROR_EVENT, { detail }));
};

const clearConnectivityError = () => {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent(API_CONNECTIVITY_CLEAR_EVENT));
};

const getHttpStatusCode = (error) => {
  const status =
    error?.response?.status ??
    error?.status ??
    error?.statusCode ??
    error?.response?.data?.status ??
    error?.response?.data?.statusCode;

  const numericStatus = Number(status);
  return Number.isNaN(numericStatus) ? null : numericStatus;
};

const getConnectivityErrorPayload = async (error) => {
  const isOffline = typeof navigator !== 'undefined' && navigator.onLine === false;
  const translatedMessages = await getConnectivityMessages();
  const status = getHttpStatusCode(error);

  if (isOffline) {
    return {
      code: CLIENT_SIDE_RESPONSE_CODES.NoInternet,
      title: translatedMessages.noInternetTitle,
    };
  }

  if (status === 502 || status === 503 || status === 504) {
    return {
      code: 'SERVER_UNAVAILABLE',
      title: translatedMessages.serverUnavailableTitle || translatedMessages.serverUnreachableTitle,
      message: translatedMessages.serverUnavailableMessage || translatedMessages.serverUnreachableMessage,
      isNetworkError: false,
      isTimeout: false,
    };
  }

  return null;
};

const instance = axios.create({
  baseURL: getApiBaseUrl() || '', // Fallback to empty string if baseURL is not ready
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

// A lightweight axios instance without interceptors for refreshing tokens
const refreshClient = axios.create();

let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

const refreshTokens = async () => {
  await loadApiConfig();
  const baseURL = getApiBaseUrl() || '';
  const refreshToken = getRefreshToken();
  const accessToken = getAccessToken();

  if (!refreshToken) {
    throw new Error('Missing refresh token');
  }

  const assignRoleKey = getAssignRoleKey();
  const tenantCode = getTenantCode();

  const response = await refreshClient.post(`${baseURL}${AUTH_REFRESH_TOKEN}`, {
    // accessToken,
    refreshToken,
    assignRoleKey: assignRoleKey || undefined,
  }, {
    headers: tenantCode ? { 'X-Tenant-Code': tenantCode } : undefined,
  });

  const data = response?.data?.data || response?.data || {};
  const responseData = response?.data || {};

  // Check for role not available error (errCode 405)
  if (responseData?.errCode === '405' || data?.errCode === '405') {
    const error = new Error('Role is not available for this user');
    error.errCode = '405';
    error.errDescription = responseData?.errDescription || data?.errDescription || 'Role is not available for this user';

    // Dispatch event to notify AppLayout.js
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent(API_ROLE_NOT_AVAILABLE_EVENT, {
        detail: {
          errCode: '405',
          errDescription: error.errDescription
        }
      }));
    }

    throw error;
  }

  const newAccessToken = data.accessToken;
  const newRefreshToken = data.refreshToken || refreshToken;
  const accessTokenExpiresAt = data.accessTokenExpiresAt;
  const refreshTokenExpiresAt = data.refreshTokenExpiresAt;

  if (!newAccessToken) {
    throw new Error('Refresh token API did not return a new access token');
  }

  // Persist updated tokens
  localStorage.setItem('accessToken', newAccessToken);
  localStorage.setItem('refreshToken', newRefreshToken);
  if (accessTokenExpiresAt) localStorage.setItem('accessTokenExpiresAt', accessTokenExpiresAt);
  if (refreshTokenExpiresAt) localStorage.setItem('refreshTokenExpiresAt', refreshTokenExpiresAt);

  return newAccessToken;
};

// Request interceptor to handle dynamic configuration
instance.interceptors.request.use(
  async (config) => {
    if (!config.headers) {
      config.headers = {};
    }

    // Ensure base URL is set
    if (!config.baseURL) {
      try {
        await loadApiConfig();
        config.baseURL = getApiBaseUrl() || '';
      } catch (error) {
        console.error('Failed to load API config:', error.message);
        throw new Error('API configuration could not be loaded');
      }
    }

    // If sending FormData, let the browser/axios set the correct multipart boundary
    if (typeof FormData !== 'undefined' && config.data instanceof FormData) {
      if (config.headers) {
        if (config.headers['Content-Type']) delete config.headers['Content-Type'];
        if (config.headers['content-type']) delete config.headers['content-type'];
      }
    }

    // Add authorization token if available
    const token = getAccessToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Set language header
    config.headers['Accept-Language'] = getCurrentLanguage();

    const tenantCode = getTenantCode();
    if (tenantCode) {
      config.headers['X-Tenant-Code'] = tenantCode;
    }

    return config;
  },
  (error) => {
    console.error('Request interceptor error:', error.message);
    return Promise.reject(error);
  }
);

// Response interceptor to handle token refresh on 401/403
instance.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const originalRequest = error.config || {};
    const status = error?.response?.status;

    // Avoid infinite loop and only handle unauthorized errors
    const isAuthEndpoint =
      originalRequest.url?.includes(AUTH_REFRESH_TOKEN) ||
      originalRequest.url?.includes('login');

    if (status === 401 && !isAuthEndpoint && !originalRequest._retry) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({
            resolve: (token) => {
              originalRequest.headers.Authorization = `Bearer ${token}`;
              resolve(instance(originalRequest));
            },
            reject,
          });
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const newToken = await refreshTokens();
        processQueue(null, newToken);
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return instance(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);

        // Don't auto-redirect for role not available error (errCode 405)
        // Let it propagate to AppLayout.js to show the banner
        if (refreshError?.errCode === '405') {
          return Promise.reject(refreshError);
        }

        try {
          // Preserve tenant/workspace code across forced session clear
          const preserved = {
            tenantCode: localStorage.getItem('tenantCode'),
          };

          localStorage.clear();
          if (typeof sessionStorage !== 'undefined') sessionStorage.clear();

          Object.entries(preserved).forEach(([key, value]) => {
            if (value) localStorage.setItem(key, value);
          });
        } catch {}

        try {
          if (typeof window !== 'undefined') {
            window.location.hash = '#/login';
            window.location.reload();
          }
        } catch {}

        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    const connectivityError = await getConnectivityErrorPayload(error);
    if (connectivityError) {
      emitConnectivityError({
        ...connectivityError,
        url: originalRequest?.url || '',
        method: originalRequest?.method || 'get',
      });
    }

    return Promise.reject(error);
  }
);

export default instance;